import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calculator } from 'lucide-react';
import { calculateRateFromTotalAmount } from '@/app/lib/purchaseInvoiceCalculations';
import { formatRateForDisplay } from '@/app/lib/salePriceRuleUtils';

interface AmountEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (totalAmount: number) => void;
  currentData: {
    quantity: number;
    taxRatePercent: number;
    discountPercent: number;
    discountAmount: number;
    isTaxInclusive: boolean;
    rateDecimalPlaces: number;
    currentAmount?: number;
  };
  itemName?: string;
}

export const AmountEntryModal: React.FC<AmountEntryModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  currentData,
  itemName,
}) => {
  const [totalAmount, setTotalAmount] = useState<string>('');
  const [calculatedRate, setCalculatedRate] = useState<number | null>(null);
  const [error, setError] = useState<string>('');

  // Initialize with current amount if available
  useEffect(() => {
    if (isOpen && currentData.currentAmount) {
      setTotalAmount(currentData.currentAmount.toFixed(2));
    }
  }, [isOpen, currentData.currentAmount]);

  // Calculate rate whenever amount changes
  useEffect(() => {
    if (!totalAmount || parseFloat(totalAmount) <= 0) {
      setCalculatedRate(null);
      setError('');
      return;
    }

    const result = calculateRateFromTotalAmount(
      totalAmount,
      currentData.quantity,
      currentData.taxRatePercent,
      currentData.discountPercent,
      currentData.discountAmount,
      currentData.isTaxInclusive,
      currentData.rateDecimalPlaces
    );

    if (result.isValid) {
      setCalculatedRate(result.rate);
      setError('');
    } else {
      setCalculatedRate(null);
      setError(result.error || 'Invalid calculation');
    }
  }, [totalAmount, currentData]);

  const handleConfirm = () => {
    const amount = parseFloat(totalAmount);
    if (!isNaN(amount) && amount > 0 && calculatedRate !== null) {
      onConfirm(amount);
      handleClose();
    }
  };

  const handleClose = () => {
    setTotalAmount('');
    setCalculatedRate(null);
    setError('');
    onClose();
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow only valid numeric input with up to 2 decimal places
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      setTotalAmount(value);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            Enter Total Amount
          </DialogTitle>
          <DialogDescription>
            Enter the total amount to calculate the unit rate automatically.
            {itemName && <span className="block mt-1 font-medium">Item: {itemName}</span>}
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-muted-foreground">Quantity</Label>
              <div className="mt-1 font-medium">{currentData.quantity}</div>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground">Tax Rate</Label>
              <div className="mt-1 font-medium">{currentData.taxRatePercent}%</div>
            </div>
          </div>
          
          {(currentData.discountPercent > 0 || currentData.discountAmount > 0) && (
            <div>
              <Label className="text-sm text-muted-foreground">Discount</Label>
              <div className="mt-1 font-medium">
                {currentData.discountAmount > 0 
                  ? `₹${currentData.discountAmount.toFixed(2)}`
                  : `${currentData.discountPercent}%`
                }
              </div>
            </div>
          )}

          <div>
            <Label htmlFor="totalAmount">Total Amount (₹)</Label>
            <Input
              id="totalAmount"
              type="text"
              value={totalAmount}
              onChange={handleAmountChange}
              placeholder="0.00"
              className="mt-1"
              autoFocus
            />
            {error && (
              <p className="text-sm text-destructive mt-1">{error}</p>
            )}
          </div>

          {calculatedRate !== null && (
            <div className="p-3 bg-muted rounded-md">
              <Label className="text-sm text-muted-foreground">Calculated Rate</Label>
              <div className="mt-1 text-lg font-semibold">
                ₹{formatRateForDisplay(calculatedRate, currentData.rateDecimalPlaces)}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirm} 
            disabled={!calculatedRate || !!error}
          >
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};